import { useEffect, useRef, useState } from "react";
import useDesignManagement from "pages/creative/pageV2/useDesignManagement";
import type { CampaignFlyerInfo } from "store/types";
import type { DesignView } from "./designSelection";

/**
 * Rehydrate the selected design from the campaign's assigned design id (flyerInfo
 * activeRoomId) for display on reload. Mirrors the ADM hydration hook against the
 * shared design-selection state. `flyerInfo` is supplied by the caller so the
 * builder mount owns the single useGetFlyerInfo call (no duplicate fetch).
 *
 * Returns `hydrated` once the assigned design has been resolved into the
 * selection. Callers use it to stop treating `activeRoomId` as "has a design"
 * after hydration, so a deliberate de-select isn't overridden by the lingering
 * server link (which a de-select doesn't clear).
 */
export const useHydrateSelectedDesign = ({
  clientId,
  flyerInfo,
  selectedDesign,
  setSelectedDesign,
}: {
  clientId?: string | null;
  flyerInfo: CampaignFlyerInfo | null;
  selectedDesign: DesignView | null;
  setSelectedDesign: (design: DesignView | null) => void;
}) => {
  const { getDesign } = useDesignManagement({ clientId: clientId || "" });
  const hydrationStarted = useRef(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (hydrationStarted.current || selectedDesign) return;
    const activeId = flyerInfo?.activeRoomId;
    if (!activeId) return;
    hydrationStarted.current = true;
    let cancelled = false;
    getDesign(activeId)
      .then((design) => {
        if (cancelled || !design) {
          hydrationStarted.current = false;
          return;
        }
        setSelectedDesign({
          id: design.id,
          designName: design.filename,
          format: design.format,
          thumbnailUrl: design.thumbnailUrl,
          s3Link: design.s3Link,
          addressBox: design.addressBox,
        });
        setHydrated(true);
      })
      .catch(() => {
        hydrationStarted.current = false;
      });
    return () => {
      cancelled = true;
    };
    // Depend only on activeRoomId (+ the selectedDesign guard): getDesign/setSelectedDesign
    // are recreated each render, and the cleanup would cancel the slow in-flight fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flyerInfo?.activeRoomId, selectedDesign]);

  return { hydrated };
};
